/**
 * Support at Home suggestion engine – modelling assumptions and exclusions for disclosure.
 */
import type { DraftFactFind } from '../constants/initialFactFind.ts';
import {
    GIFTING_LIMIT_ANNUAL,
    GIFTING_LIMIT_5_YEAR,
    DOWNSIZER_CONTRIBUTION_CAP,
    FUNERAL_BOND_LIMIT
} from '../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import { SAH_EMERGENCY_FUND_RESERVE, listClientLiabilities } from './supportAtHomeStrategyFunding.ts';
import { PLAN_CHOICE_RULE_LINES } from './planChoiceRule.ts';
import { buildDownsizeAssumptionLines, type DownsizePropertySettings } from './supportAtHomeDownsizeScenarioUtils.ts';

/*
 * ONE FUNERAL BOND LIMIT, FROM THE RATES FILE.
 *
 * This was a local `const FUNERAL_BOND_EXEMPT_LIMIT = 15000`, shadowing the published figure of
 * $15,750 that the assessment itself uses. So the engine sized its funeral bond candidates $750 per
 * person short of the exemption, and the screen told the client a limit the assessment disagreed
 * with. A rate that lives in two places will eventually differ in two places — this one already did.
 */
const FUNERAL_BOND_EXEMPT_LIMIT = FUNERAL_BOND_LIMIT;

export interface SupportAtHomeStrategySettings {
    includeGifting?: boolean;
    includeFuneralBonds?: boolean;
    includeAnnuities?: boolean;
    includePropertyStrategies?: boolean;
    includeDebtRepayment?: boolean;
    propertyStrategies?: DownsizePropertySettings;
    [key: string]: unknown;
}

export interface AssumptionsSection {
    category: string;
    items: string[];
}

export interface SupportAtHomeAssumptionsSummary {
    legacyYears: number;
    emergencyFund: number;
    totalDebt: number;
    liabilityCount: number;
    enabledStrategyTypes: string[];
}

export interface SupportAtHomeAssumptions {
    modelled: AssumptionsSection[];
    excluded: string[];
    summary: SupportAtHomeAssumptionsSummary;
}

export interface BuildSupportAtHomeAssumptionsParams {
    factFind: DraftFactFind;
    strategySettings?: SupportAtHomeStrategySettings;
    legacyYears?: number;
    emergencyFund?: number;
    agedCareIntegrationApplied?: boolean;
    usesEstimatedServicePlan?: boolean;
    estimatedServicePlanDescription?: string | null;
}

/**
 * Build assumptions / exclusions sections shown after analysis runs.
 */
export function buildSupportAtHomeAssumptions({
    factFind,
    strategySettings = {},
    legacyYears = 5,
    emergencyFund = SAH_EMERGENCY_FUND_RESERVE,
    agedCareIntegrationApplied = false,
    usesEstimatedServicePlan = false,
    estimatedServicePlanDescription = null,
}: BuildSupportAtHomeAssumptionsParams): SupportAtHomeAssumptions {
    const isPartnered = factFind?.adviceFor === 'couple';
    const liabilities = listClientLiabilities(factFind);
    const totalDebt = liabilities.reduce((sum, l) => sum + l.balance, 0);

    const modelled: AssumptionsSection[] = [
        {
            category: 'Scope',
            items: [
                'Each selected strategy is modelled individually against your current baseline (not combined multi-strategy portfolios).',
                `Projections run for ${legacyYears} year${legacyYears === 1 ? '' : 's'} using the same manual-control engine as the rest of the app.`,
                agedCareIntegrationApplied
                    ? 'A linked aged care scenario has been applied to the baseline before comparing Support at Home options.'
                    : 'Baseline uses your current Client Profile unless a linked aged care scenario is selected.',
                usesEstimatedServicePlan
                    ? `Support at Home service costs use an illustrative service mix (not your Service Builder plan): ${estimatedServicePlanDescription || 'moderate personal care, domestic, social, transport and meals package at catalogue median prices.'}`
                    : 'Support at Home service costs use your saved Service Builder plan at modelled contribution rates.',
            ],
        },
        {
            // The same rule and words in every module (planChoiceRule.ts, owner 20 Oct 2026).
            category: 'How the plans are chosen',
            items: PLAN_CHOICE_RULE_LINES.map((r) => `${r.label}: ${r.text}`),
        },
        {
            category: 'Liquidity & funding',
            items: [
                `$${emergencyFund.toLocaleString()} cash reserve is preserved on bank accounts when liquidating for gifting, annuities, funeral bonds, renovations, upgrades, and debt repayment.`,
                'Multi-asset funding is used where needed (bank accounts first, then account-based pensions up to 50% of balance, then shares).',
                'Capital gains tax on share or investment property sales uses a simplified marginal-rate estimate (50% CGT discount assumed where applicable).',
            ],
        },
        {
            category: 'Centrelink & gifting',
            items: [
                `Gifting strategies respect remaining capacity under $${GIFTING_LIMIT_ANNUAL.toLocaleString()} per financial year and $${GIFTING_LIMIT_5_YEAR.toLocaleString()} over five years, including gifts already recorded in your profile.`,
                'New strategy gifts are recorded in giftsHistory and flow through deprivation calculations.',
                `Funeral bond strategies respect the $${FUNERAL_BOND_EXEMPT_LIMIT.toLocaleString()} per person exempt asset limit${isPartnered ? ' (combined for couples where applicable)' : ''}.`,
            ],
        },
        {
            category: 'Property strategies',
            items: [
                ...buildDownsizeAssumptionLines(factFind, strategySettings.propertyStrategies || {}),
                'Downsize surplus allocated per scenario to bank, downsizer super (if age 55+), and/or debt repayment.',
                `Downsizer contributions capped at $${DOWNSIZER_CONTRIBUTION_CAP.toLocaleString()} per eligible person.`,
                'Where age-eligible (60+), downsizer scenarios may commence an account-based pension at legislative minimum drawdown; otherwise proceeds remain in accumulation super.',
                'Increase exempt home equity: liquid assets converted to principal home at $1 for $1 (renovate in place or buy up — same modelling in the suggestion engine; not a market valuation).',
                'Manual Control upgrade path may model sell/buy costs separately when configured there.',
                'Support at Home cashflow uses your modelled contribution dollars (contribution rates × illustrative or saved service plan), not gross provider catalogue fees.',
                'Reverse mortgage / HEAS: lump-sum proceeds add to liquid assets; loan balance compounds at the indicative rate in projections (matching Manual Control). HEAS payment drawdown uses indicative rates (not provider-specific quotes).',
            ],
        },
        {
            category: 'Annuities',
            items: [
                'Lifetime annuities use indicative actuarial rates; purchase amount equals actual funded liquidation (not an unfunded notional amount).',
                'Assessable income/asset treatment follows post-2019 lifetime annuity rules (60% income / 60% or 30% asset test).',
                'No indexation or residual capital value unless manually configured elsewhere.',
            ],
        },
    ];

    if (liabilities.length > 0 && strategySettings.includeDebtRepayment !== false) {
        modelled.push({
            category: 'Debt repayment',
            items: [
                `Client liabilities totalling $${Math.round(totalDebt).toLocaleString()} were identified.`,
                'Debt repayment strategies liquidate assessable assets to reduce loan/mortgage balances; interest savings are reflected via reduced liability balances in projections.',
                'Downsize scenarios may also allocate sale proceeds to debt repayment before surplus cash or downsizer contributions.',
            ],
        });
    }

    const excluded: string[] = [
        'Combined multi-strategy portfolios (e.g. gifting plus annuity in the same scenario) — each option is tested in isolation.',
        'Oct 2026 Support at Home contribution rule changes (not yet in production rates).',
        'Detailed living-arrangement fact-find variations beyond current profile / linked aged care context.',
        'Provider-specific annuity quotes, reverse mortgage product features, or HEAS eligibility assessments.',
        'Stamp duty, legal fees, and moving costs on property transactions beyond the 2.5% selling-cost assumption.',
        'Superannuation contribution cap breaches, Division 293 tax, or personal financial advice suitability assessment.',
        'Changes to legislation, deeming rates, or pension rates beyond current app constants.',
        'Tax advice on structured products, trust distributions, or complex ownership structures.',
    ];

    if (liabilities.length === 0) {
        excluded.push('Debt repayment strategies — no liabilities with a positive balance were recorded in the Client Profile.');
    }

    if (!strategySettings.includeGifting) {
        excluded.push('Gifting strategies — not selected for this analysis run.');
    }
    if (!strategySettings.includeAnnuities) {
        excluded.push('Annuity strategies — not selected for this analysis run.');
    }
    if (!strategySettings.includePropertyStrategies) {
        excluded.push('Property strategies — not selected for this analysis run.');
    }

    const enabledTypes: string[] = [];
    if (strategySettings.includeGifting) enabledTypes.push('gifting');
    if (strategySettings.includeFuneralBonds) enabledTypes.push('funeral bonds');
    if (strategySettings.includeAnnuities) enabledTypes.push('annuities');
    if (strategySettings.includeDebtRepayment !== false && liabilities.length > 0) enabledTypes.push('debt repayment');
    if (strategySettings.includePropertyStrategies) {
        const ps = strategySettings.propertyStrategies || {};
        if (ps.downsize) enabledTypes.push('downsize');
        if (ps.increaseHomeEquity || ps.renovate || ps.upgrade) enabledTypes.push('increase home equity');
        if (ps.reverseMortgage) enabledTypes.push('reverse mortgage');
        if (ps.heas) enabledTypes.push('HEAS');
    }

    return {
        modelled,
        excluded,
        summary: {
            legacyYears,
            emergencyFund,
            totalDebt,
            liabilityCount: liabilities.length,
            enabledStrategyTypes: enabledTypes,
        },
    };
}

export interface AssumptionsModalBullet {
    category: string;
    text: string;
}

/**
 * Flatten structured assumptions for the Assumptions modal (Aged Care–style bullet list).
 */
export function flattenSupportAtHomeAssumptionsForModal(modellingAssumptions: SupportAtHomeAssumptions | null | undefined): AssumptionsModalBullet[] {
    if (!modellingAssumptions) return [];
    const bullets: AssumptionsModalBullet[] = [];
    (modellingAssumptions.modelled || []).forEach((section) => {
        (section.items || []).forEach((item) => {
            bullets.push({ category: section.category, text: item });
        });
    });
    (modellingAssumptions.excluded || []).forEach((item) => {
        bullets.push({ category: 'Not factored in', text: item });
    });
    return bullets;
}
